import { useState, useEffect, useRef } from 'react'
import cx from 'classnames'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import MailOutlineIcon from '@material-ui/icons/MailOutline'
import { Translate } from 'react-localize-redux'
import { TextField, IconButton, FormControlLabel, Checkbox } from '@material-ui/core'
import Tooltip from '@mui/material/Tooltip'

import { useAlertForm } from '../AlertFormProvider'
import CircularAddButton from 'common/components/CircularAddButton/CircularAddButton'
import {
  appendAlertRecipient,
  getProjectAlertRecipients,
  truncateEmail
} from 'utils/alertRecipients'
import { EMAIL_REGEX } from 'utils/validation'
import styles from '../ParameterAlertForm.module.scss'
import stepStyles from '../steps/Settings/Settings.module.scss'

// Self-contained recipient selector for the Settings step.
// Owns its own state for the loaded recipient list and the add-email input;
// the parent controls only the {email: boolean} selection map so submit can
// read it. The "anyEmailRequired" error message and clearing it on selection
// stay outside this component (parent's RHF error state).
const EmailRecipients = ({
  selectedEmails,
  onSelectionChange,
  onRecipientsChange = () => {},
  showError
}) => {
  const {
    state: { isAddingEmail },
    actions,
    meta: { translate, user, projectId, projects }
  } = useAlertForm()

  const [ownRecipient, setOwnRecipient] = useState(null)
  const [searched, setSearched] = useState([])
  const [emailForAdd, setEmailForAdd] = useState('')
  const [openInfoE, setOpenInfoE] = useState(false)
  const [invalidFormat, setInvalidFormat] = useState(false)
  const infoDivRef = useRef(null)
  const buttonRef = useRef(null)

  // Populate recipients from the project list whenever projects or the user changes
  useEffect(() => {
    const { ownRecipient: own, recipients } = getProjectAlertRecipients(projects, user.toJS().email)
    setOwnRecipient(own)
    setSearched(recipients)
  }, [projects, user])

  // Report the current valid recipient emails upward so submit can drop any
  // stale selection (for example a recipient removed elsewhere while editing).
  useEffect(() => {
    const validEmails = [ownRecipient?.email, ...searched.map(recipient => recipient.email)].filter(
      Boolean
    )
    onRecipientsChange(validEmails)
  }, [ownRecipient, searched, onRecipientsChange])

  // Close the info panel when the user clicks outside of it
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        infoDivRef.current &&
        !infoDivRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setOpenInfoE(false)
      }
    }
    if (openInfoE) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openInfoE])

  const handleChangeAddEmail = event => {
    setEmailForAdd(event.target.value)
    if (event.target.value !== '') setInvalidFormat(false)
  }

  const handleChangeSelectedEmails = event => {
    onSelectionChange({ ...selectedEmails, [event.target.value]: event.target.checked })
  }

  // Delete a recipient and drop it from the selection map at the same time.
  // Otherwise a deleted email stays selected and gets sent to the backend, and
  // it also hides the "recipient required" error even with no valid selection.
  const handleDeleteRecipient = email => {
    const next = { ...selectedEmails }
    delete next[email]
    onSelectionChange(next)
    actions.onDeleteAlertRecipient(email)
  }

  const onClickAdd = () => {
    const email = emailForAdd.trim().toLowerCase()
    if (email !== '' && EMAIL_REGEX.test(email)) {
      actions.addAlertRecipient({
        email,
        projectId,
        customerId: user.toJS()._id
      })
      // Update local list immediately — the backend confirms later
      if (email === user.toJS().email.toLowerCase()) {
        setOwnRecipient({ email, confirmed: false })
      } else {
        setSearched(appendAlertRecipient(searched, email))
      }
      // Select the freshly added email by default and clear the search input
      // so the full recipient list becomes visible again
      onSelectionChange({ ...selectedEmails, [email]: true })
      setEmailForAdd('')
    } else {
      setInvalidFormat(true)
    }
  }

  const emailQuery = emailForAdd.trim().toLowerCase()
  // Substring match: drives which recipients stay visible while typing.
  const matchesQuery = recipient => recipient?.email?.toLowerCase().includes(emailQuery)
  // Exact match: drives the add-button gate. Kept separate from matchesQuery so a
  // valid distinct email (e.g. "john@x.com") is not blocked just because it is a
  // substring of an existing one ("ajohn@x.com").
  const isExistingRecipient =
    ownRecipient?.email?.toLowerCase() === emailQuery ||
    searched.some(recipient => recipient?.email?.toLowerCase() === emailQuery)

  const visibleOwnRecipient = !emailQuery || matchesQuery(ownRecipient) ? ownRecipient : null
  const visibleRecipients = emailQuery ? searched.filter(matchesQuery) : searched

  return (
    <>
      <div className={stepStyles.emailreceiver}>
        <div className={styles.dataInput}>
          {translate('addParameterAlertModal.selectEmailsForAlerts')}
          <div className={styles.wrapperInfoButton}>
            <IconButton
              ref={buttonRef}
              className={styles.infoButton}
              variant='outlined'
              onClick={() => setOpenInfoE(!openInfoE)}
              value=' '
              name='infoParam'
              disableRipple
            >
              <InfoOutlinedIcon />
            </IconButton>
          </div>
        </div>
        <div className={styles.wrapperDataInfo}>
          {openInfoE && (
            <div
              ref={infoDivRef}
              className={stepStyles.cajaEmail}
            >
              <Translate
                id='emailInfoN'
                options={{ renderInnerHtml: true }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add-email row */}
      <div className={stepStyles.emailSearchRow}>
        <TextField
          variant='outlined'
          required
          fullWidth
          id='emailForAdd'
          onChange={handleChangeAddEmail}
          label={translate('addParameterAlertModal.searchOrAddEmail')}
          onKeyPress={e => {
            e.key === 'Enter' && e.preventDefault()
          }}
          name='emailForAdd'
          autoComplete='emailForAdd'
          value={emailForAdd}
          helperText={`${emailForAdd.length}/128`}
          inputProps={{ maxLength: 128 }}
          className={cx(styles.input, stepStyles.emailSearchInput, {
            [stepStyles.hasError]: invalidFormat
          })}
        />
        <CircularAddButton
          ariaLabel={translate('add')}
          className={stepStyles.emailSearchButton}
          disabled={!EMAIL_REGEX.test(emailQuery) || isExistingRecipient || isAddingEmail}
          isLoading={isAddingEmail}
          onClick={onClickAdd}
        />
      </div>
      {invalidFormat && (
        <div className={styles.error}>
          <small>{translate('validation.inValidEmail')}</small>
        </div>
      )}

      {/* Recipient list — own email first, then other project recipients */}
      <div className={stepStyles.checkboxEmailWrapper}>
        {visibleOwnRecipient && (
          <div className={styles.checkboxFormDiv}>
            <FormControlLabel
              className={cx(
                styles.checkboxForm,
                visibleOwnRecipient.confirmed ? stepStyles.confirmed : stepStyles.notConfirmed
              )}
              control={
                <Checkbox
                  className={cx(styles.checkbox)}
                  id={visibleOwnRecipient.email}
                  onChange={handleChangeSelectedEmails}
                  classes={{ checked: styles.checkedBox }}
                  required
                  checked={selectedEmails[visibleOwnRecipient.email] === true}
                  name='emailRecipients'
                  value={visibleOwnRecipient.email}
                  disableRipple
                />
              }
              label={visibleOwnRecipient.email + ' ' + translate('thisUser')}
            />
          </div>
        )}
        {visibleRecipients.map((recipient, i) => (
          <div
            key={i + 'emailData'}
            className={styles.checkboxFormDiv}
          >
            <FormControlLabel
              className={cx(
                styles.checkboxForm,
                recipient.confirmed ? stepStyles.confirmed : stepStyles.notConfirmed
              )}
              control={
                <Checkbox
                  className={cx(styles.checkbox)}
                  id={recipient.email}
                  onChange={handleChangeSelectedEmails}
                  classes={{ checked: styles.checkedBox }}
                  required
                  checked={selectedEmails[recipient.email] === true}
                  name='emailRecipients'
                  value={recipient.email}
                  disableRipple
                />
              }
              label={
                <Tooltip title={recipient.email}>
                  <span>{truncateEmail(recipient.email)}</span>
                </Tooltip>
              }
            />
            <div className={stepStyles.recipientActions}>
              {!recipient.confirmed && (
                <Tooltip title={translate('resendCode')}>
                  <IconButton
                    onClick={() =>
                      actions.addAlertRecipient({
                        email: recipient.email,
                        projectId,
                        customerId: user.toJS()._id,
                        isResend: true
                      })
                    }
                    className={stepStyles.resendEmailRecipientIcon}
                    size='small'
                  >
                    <MailOutlineIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                onClick={() => handleDeleteRecipient(recipient.email)}
                className={stepStyles.deleteEmailRecipientIcon}
                size='small'
              >
                <DeleteOutlineIcon fontSize='small' />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
      {showError && (
        <div className={styles.error}>
          <small>{translate('validation.anyEmailRequired')}</small>
        </div>
      )}
    </>
  )
}

export default EmailRecipients
